'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { motion } from 'framer-motion';
import { Clock, Search, ArrowUpRight } from 'lucide-react';
import { usePublishedArticles } from './usePublishedArticles';

const categories = [
  'All',
  'Marketing Strategy',
  'Paid Advertising',
  'Business Growth',
  'Brand Positioning',
  'SEO & Content',
  'Business Consulting',
];

export default function InsightsListClient() {
  const [activeCategory, setActiveCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const articles = usePublishedArticles();

  const filtered = articles?.filter((a) => {
    const matchCat = activeCategory === 'All' || a?.category === activeCategory;
    const matchSearch =
      searchQuery === '' ||
      a?.title?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      a?.excerpt?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    return matchCat && matchSearch;
  });

  return (
    <div className="min-h-screen m-ground">
      {/* Hero */}
      <section data-ground className="pt-32 pb-16" style={{ background: 'var(--m-ink)' }}>
        <div className="m-container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center gap-3 mb-6">
              <div style={{ width: 24, height: 1, background: 'rgba(var(--m-on-ink-rgb),0.25)' }} />
              <span
                className="font-mono uppercase"
                style={{
                  fontSize: 9,
                  color: 'rgba(var(--m-on-ink-rgb),0.4)',
                  letterSpacing: '0.22em',
                }}
              >
                INS — Insights &amp; Resources
              </span>
            </div>
            <h1
              className="font-bold uppercase leading-none mb-5"
              style={{
                fontFamily: "'Archivo Narrow', 'Roboto Condensed', sans-serif",
                fontSize: 'clamp(32px, 5vw, 64px)',
                color: 'var(--m-bg)',
                letterSpacing: '0.02em',
                lineHeight: 1.0,
              }}
            >
              Practical guides
              <br />
              for business growth
            </h1>
            <p
              className="text-sm leading-relaxed max-w-md"
              style={{ color: 'rgba(var(--m-on-ink-rgb),0.5)', lineHeight: 1.7 }}
            >
              No fluff. Just actionable insights from our work with SMBs across Europe and
              Australia.
            </p>
          </motion.div>
        </div>
      </section>

      <div data-ground className="m-container py-12">
        {/* Filters */}
        <div
          className="flex flex-col sm:flex-row gap-4 mb-12 pb-8"
          style={{ borderBottom: '1px solid rgba(var(--m-ink-rgb),0.08)' }}
        >
          {/* Search */}
          <div className="relative flex-1 max-w-xs">
            <Search
              className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5"
              style={{ color: 'var(--m-muted-3)' }}
            />
            <input
              type="text"
              placeholder="Search articles..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e?.target?.value)}
              className="w-full pl-9 pr-4 py-2.5 text-sm focus:outline-none transition-colors"
              style={{
                background: 'var(--m-bg)',
                border: '1px solid rgba(var(--m-ink-rgb),0.1)',
                color: 'var(--m-ink)',
                fontFamily: 'inherit',
              }}
            />
          </div>
          {/* Category filters */}
          <div className="flex flex-wrap gap-2">
            {categories?.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className="font-mono uppercase transition-all"
                style={{
                  fontSize: 9,
                  letterSpacing: '0.12em',
                  padding: '6px 12px',
                  border: '1px solid',
                  borderColor:
                    activeCategory === cat ? 'var(--m-ink)' : 'rgba(var(--m-ink-rgb),0.12)',
                  background: activeCategory === cat ? 'var(--m-ink)' : 'transparent',
                  color: activeCategory === cat ? 'var(--m-bg)' : 'var(--m-ink-6)',
                  cursor: 'pointer',
                }}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        {/* Articles grid */}
        {filtered?.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-sm font-mono" style={{ color: 'var(--m-muted-3)' }}>
              No articles found matching your search.
            </p>
          </div>
        ) : (
          /* The gaps are the rules: a 1px background showing between tiles is
             what separates one card from the next. The border closes the same
             frame on the outside — without it the grid dissolves into the page
             at its edges, which it did the moment the ground became white. */
          <div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px"
            style={{
              background: 'rgba(var(--m-ink-rgb),0.08)',
              border: '1px solid rgba(var(--m-ink-rgb),0.08)',
            }}
          >
            {filtered?.map((article, i) => (
              <motion.article
                key={article?.id}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.06, duration: 0.5 }}
                className="group"
              >
                <Link href={`/insights/${article?.slug}`} className="block h-full">
                  <div
                    className="h-full transition-all duration-300"
                    style={{ background: 'var(--m-bg)' }}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLElement).style.background = 'var(--m-surface-2)';
                      (e.currentTarget as HTMLElement).style.boxShadow =
                        '0 4px 24px rgba(var(--m-ink-rgb),0.07)';
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLElement).style.background = 'var(--m-bg)';
                      (e.currentTarget as HTMLElement).style.boxShadow = 'none';
                    }}
                  >
                    {/* Card header */}
                    <div
                      className="p-6 pb-5"
                      style={{
                        background: 'rgba(var(--m-ink-rgb),0.02)',
                        borderBottom: '1px solid rgba(var(--m-ink-rgb),0.06)',
                      }}
                    >
                      <div className="flex items-start justify-between mb-5">
                        <span
                          className="inline-flex items-center font-mono uppercase"
                          style={{
                            fontSize: 9,
                            letterSpacing: '0.12em',
                            padding: '4px 10px',
                            border: '1px solid rgba(var(--m-ink-rgb),0.12)',
                            color: 'var(--m-ink-6)',
                            background: 'var(--m-bg)',
                          }}
                        >
                          {article?.category}
                        </span>
                        <span
                          className="font-mono font-bold select-none"
                          style={{
                            fontSize: 36,
                            color: 'rgba(var(--m-ink-rgb),0.05)',
                            lineHeight: 1,
                          }}
                        >
                          {String(i + 1).padStart(3, '0')}
                        </span>
                      </div>
                      <h2
                        className="font-bold leading-snug transition-colors"
                        style={{
                          fontFamily: "'Archivo Narrow', 'Roboto Condensed', sans-serif",
                          fontSize: 'clamp(14px, 1.2vw, 17px)',
                          color: 'var(--m-ink)',
                          lineHeight: 1.35,
                          letterSpacing: '0.01em',
                        }}
                      >
                        {article?.title}
                      </h2>
                    </div>

                    <div className="p-6">
                      <p
                        className="text-sm leading-relaxed mb-6"
                        style={{ color: 'var(--m-ink-6)', lineHeight: 1.7 }}
                      >
                        {article?.excerpt}
                      </p>
                      <div className="flex items-center justify-between">
                        <div
                          className="flex items-center gap-1.5 font-mono"
                          style={{ fontSize: 11, color: 'var(--m-muted-3)' }}
                        >
                          <Clock className="w-3.5 h-3.5" />
                          {article?.readTime}
                        </div>
                        <div
                          className="flex items-center gap-1.5 font-mono transition-colors group-hover:text-gray-900"
                          style={{ fontSize: 11, color: 'var(--m-muted-3)' }}
                        >
                          Read article <ArrowUpRight className="w-3.5 h-3.5" />
                        </div>
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.article>
            ))}
          </div>
        )}

        {/* Newsletter CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mt-20 p-8 lg:p-12"
          style={{ background: 'var(--m-ink)' }}
        >
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div
                  style={{ width: 20, height: 1, background: 'rgba(var(--m-on-ink-rgb),0.2)' }}
                />
                <span
                  className="font-mono uppercase"
                  style={{
                    fontSize: 9,
                    color: 'rgba(var(--m-on-ink-rgb),0.35)',
                    letterSpacing: '0.22em',
                  }}
                >
                  NWS — Newsletter
                </span>
              </div>
              <h2
                className="font-bold uppercase leading-none mb-3"
                style={{
                  fontFamily: "'Archivo Narrow', 'Roboto Condensed', sans-serif",
                  fontSize: 'clamp(22px, 2.5vw, 32px)',
                  color: 'var(--m-bg)',
                  letterSpacing: '0.02em',
                }}
              >
                Get insights in your inbox
              </h2>
              <p
                className="text-sm"
                style={{ color: 'rgba(var(--m-on-ink-rgb),0.45)', lineHeight: 1.7, maxWidth: 360 }}
              >
                Practical business and marketing guides, delivered monthly. No spam — unsubscribe
                any time.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 lg:min-w-[380px]">
              <input
                type="email"
                placeholder="Your work email"
                className="flex-1 px-4 py-3 text-sm focus:outline-none transition-colors"
                style={{
                  background: 'rgba(var(--m-on-ink-rgb),0.06)',
                  border: '1px solid rgba(var(--m-on-ink-rgb),0.12)',
                  color: 'var(--m-bg)',
                  fontFamily: 'inherit',
                }}
              />
              <button
                className="flex items-center justify-center gap-2 font-mono uppercase transition-all whitespace-nowrap px-6 py-3"
                style={{
                  background: 'var(--m-bg)',
                  color: 'var(--m-ink)',
                  fontSize: 10,
                  letterSpacing: '0.12em',
                  border: '1px solid var(--m-bg)',
                  cursor: 'pointer',
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.background = 'transparent';
                  (e.currentTarget as HTMLElement).style.color = 'var(--m-bg)';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.background = 'var(--m-bg)';
                  (e.currentTarget as HTMLElement).style.color = 'var(--m-ink)';
                }}
              >
                Subscribe
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
